import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/dist/query';
import type {
  CreatePanelApiArg,
  CreatePanelApiResponse,
  CreateQuickViewApiArg,
  CreateQuickViewApiResponse,
  Dashboard,
  DeletePanelApiArg,
  DeletePanelApiResponse,
  DeleteQuickViewApiArg,
  DeleteQuickViewApiResponse,
  QuickView,
  ReadComparisonInstrumentsApiArg,
  ReadComparisonInstrumentsApiResponse,
  ReadDashboardsApiResponse,
  ReadFiltersApiArg,
  ReadFiltersApiResponse,
  ReadInstrumentsApiArg,
  ReadInstrumentsApiResponse,
  ReadPeriodPresetsApiArg,
  ReadPeriodPresetsApiResponse,
  ReadQuickViewApiArg,
  ReadQuickViewApiResponse,
  ReadQuickViewsApiArg,
  ReadQuickViewsApiResponse,
  ReadRewindDataApiArg,
  ReadRewindDataApiResponse,
  UpdateQuickViewApiArg,
  UpdateQuickViewApiResponse,
  UpdateQuickViewFavoriteStateApiArg,
  UpdateQuickViewFavoriteStateApiResponse,
  UpdateQuickViewPanelsApiArg,
  UpdateQuickViewPanelsApiResponse,
} from 'lib-analytics-types';
import { paginatedQuery } from 'lib-shared-api-utils';
import unionBy from 'lodash/unionBy';
import moment from 'moment-timezone';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import {
  transformCreateUpdateQuickViewArg,
  transformReadInstrumentsArg,
  transformUpdateQuickViewPanelsArg,
} from './utils/transformArg';
import {
  transformReadDashboardsResponse,
  transformReadFiltersResponse,
  transformReadInstrumentsResponse,
  transformReadQuickViewResponse,
  transformReadQuickViewsResponse,
} from './utils/transformResponse';

export const analyticsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readDashboards: builder.query<ReadDashboardsApiResponse, void>({
      query: () => ({
        version: 1,
        path: 'analytics/dashboards',
      }),
      transformResponse: transformReadDashboardsResponse,
    }),
    readFilters: builder.query<ReadFiltersApiResponse, ReadFiltersApiArg>({
      query: ({ id, ...body }) => ({
        version: 1,
        path: `analytics/dashboards/${id}/filters`,
        method: 'POST',
        body,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const {
            data: {
              data: { filters },
            },
          } = await queryFulfilled;

          // We're adding the filters data to the corresponding dashboard in the `readDashboards`
          // query state, so that we have easy access to them when working with the dashboard.
          dispatch(
            analyticsApi.util.updateQueryData(
              'readDashboards',
              undefined,
              (draft) => {
                const dashboard = draft.data.dashboards.find(
                  (d) => d.id === id,
                ) as DeepWritable<Dashboard>;

                if (dashboard) {
                  dashboard.filters = unionBy(
                    filters as DeepWritable<typeof filters>,
                    dashboard.filters,
                    'field',
                  );
                }
              },
            ),
          );
        } catch {
          /* do nothing */
        }
      },
      transformResponse: transformReadFiltersResponse,
    }),
    readInstruments: builder.query<
      ReadInstrumentsApiResponse,
      ReadInstrumentsApiArg
    >({
      query: (arg) => ({
        version: 1,
        path: `analytics/panels/${arg.id}/instruments`,
        method: 'POST',
        body: transformReadInstrumentsArg(arg),
      }),
      transformResponse: transformReadInstrumentsResponse,
    }),
    readComparisonInstruments: builder.query<
      ReadComparisonInstrumentsApiResponse,
      ReadComparisonInstrumentsApiArg
    >({
      query: ({ payload }) => ({
        version: 1,
        path: `analytics/panels/${payload.id}/instruments`,
        method: 'POST',
        body: transformReadInstrumentsArg(payload),
      }),
      serializeQueryArgs: ({
        queryArgs: { side },
        endpointDefinition,
        endpointName,
      }) => {
        return defaultSerializeQueryArgs({
          endpointName,
          queryArgs: { side },
          endpointDefinition,
        });
      },
      // Removes the cached data immediately after the last component unsubscribes. This is
      // necessary to ensure that no stale data remains when the user compares different panels.
      keepUnusedDataFor: 0,
    }),
    readPeriodPresets: builder.query<
      ReadPeriodPresetsApiResponse,
      ReadPeriodPresetsApiArg
    >({
      query: ({ timezone }) => ({
        version: 1,
        path: `analytics/presets`,
        method: 'POST',
        body: {
          timezone: timezone ?? moment.tz.guess(),
        },
      }),
    }),
    readQuickViews: builder.query<
      ReadQuickViewsApiResponse,
      ReadQuickViewsApiArg
    >({
      query: (arg) =>
        paginatedQuery(arg, ({ type = 'all' }) => ({
          version: 1,
          path: `analytics/quick-views`,
          params: {
            type,
          },
        })),
      transformResponse: transformReadQuickViewsResponse,
    }),
    readFavoriteQuickViews: builder.query<ReadQuickViewsApiResponse, void>({
      query: () =>
        paginatedQuery(
          {
            limit: 20,
            offset: 0,
            sort: {
              key: 'name',
              direction: 'asc',
            },
          },
          () => ({
            version: 1,
            path: `analytics/quick-views`,
            params: {
              type: 'favorite',
            },
          }),
        ),
      transformResponse: transformReadQuickViewsResponse,
    }),
    readQuickView: builder.query<ReadQuickViewApiResponse, ReadQuickViewApiArg>(
      {
        query: ({ id }) => ({
          version: 1,
          path: `analytics/quick-views/${id}`,
        }),
        transformResponse: transformReadQuickViewResponse,
      },
    ),
    createQuickView: builder.mutation<
      CreateQuickViewApiResponse,
      CreateQuickViewApiArg
    >({
      query: ({ quickView }) => ({
        version: 1,
        path: `analytics/quick-views`,
        method: 'POST',
        body: {
          ...transformCreateUpdateQuickViewArg(quickView),
        },
      }),
      transformResponse: transformReadQuickViewResponse,
    }),
    updateQuickView: builder.mutation<
      UpdateQuickViewApiResponse,
      UpdateQuickViewApiArg
    >({
      query: ({ id, userId, quickView }) => ({
        version: 1,
        path: `analytics/quick-views/${id}`,
        method: 'PATCH',
        body: {
          ...transformCreateUpdateQuickViewArg(quickView),
          user_id: userId,
        },
      }),
      transformResponse: transformReadQuickViewResponse,
      // Handles pessimistic update of the cached API data.
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const {
            data: {
              data: { quick_view: updatedQuickView },
            },
          } = await queryFulfilled;

          dispatch(
            analyticsApi.util.updateQueryData(
              'readFavoriteQuickViews',
              undefined,
              (draft) => {
                const favoritedQuickViews = draft.data
                  .quick_views as DeepWritable<typeof draft.data.quick_views>;
                const index = draft.data.quick_views.findIndex(
                  (_quickView) => _quickView.id === id,
                );

                if (index !== -1) {
                  favoritedQuickViews[index] =
                    updatedQuickView as DeepWritable<QuickView>;
                }
              },
            ),
          );

          dispatch(
            analyticsApi.util.updateQueryData(
              'readQuickView',
              { id },
              (draft) => {
                const mutableQuickView = draft.data.quick_view as DeepWritable<
                  typeof draft.data.quick_view
                >;

                // Panels cannot be updated through the PATCH endpoint, so we omit them from this
                // update to avoid overwriting existing data and causing unnecessary re-renders.
                const { panels, ...fieldsToUpdate } = updatedQuickView;
                Object.assign(mutableQuickView, fieldsToUpdate);
              },
            ),
          );
        } catch (error) {
          /** Do nothing */
        }
      },
    }),
    deleteQuickView: builder.mutation<
      DeleteQuickViewApiResponse,
      DeleteQuickViewApiArg
    >({
      query: ({ id }) => ({
        version: 1,
        path: `analytics/quick-views/${id}`,
        method: 'DELETE',
      }),
      // Handles optimistic update of the cached API data.
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchReadFavoriteQuickViews = dispatch(
          analyticsApi.util.updateQueryData(
            'readFavoriteQuickViews',
            undefined,
            (draft) => {
              const mutableQuickViews = draft.data.quick_views as DeepWritable<
                typeof draft.data.quick_views
              >;
              const index = draft.data.quick_views.findIndex(
                (_quickView) => _quickView.id === id,
              );

              if (index !== -1) {
                mutableQuickViews.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadFavoriteQuickViews.undo();
        }
      },
    }),
    updateQuickViewFavoriteState: builder.mutation<
      UpdateQuickViewFavoriteStateApiResponse,
      UpdateQuickViewFavoriteStateApiArg
    >({
      query: ({ quickView, favorited }) => ({
        version: 1,
        path: `analytics/quick-views/${quickView.id}/favorite`,
        method: favorited ? 'POST' : 'DELETE',
      }),
      // Handles optimistic update of the cached API data.
      async onQueryStarted(
        { quickView, favorited },
        { dispatch, queryFulfilled },
      ) {
        const patchReadFavoriteQuickViews = dispatch(
          analyticsApi.util.updateQueryData(
            'readFavoriteQuickViews',
            undefined,
            (draft) => {
              const mutableQuickViews = draft.data.quick_views as DeepWritable<
                QuickView[]
              >;

              if (favorited) {
                mutableQuickViews.push({
                  ...(quickView as DeepWritable<QuickView>),
                  favorited,
                });
              } else {
                mutableQuickViews.splice(
                  mutableQuickViews.findIndex(({ id }) => id === quickView.id),
                  1,
                );
              }
            },
          ),
        );

        const patchReadQuickView = dispatch(
          analyticsApi.util.updateQueryData(
            'readQuickView',
            { id: quickView.id },
            (draft) => {
              const mutableQuickView = draft.data.quick_view as DeepWritable<
                typeof draft.data.quick_view
              >;

              mutableQuickView.favorited = favorited;
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadFavoriteQuickViews.undo();
          patchReadQuickView.undo();
        }
      },
    }),
    updateQuickViewPanels: builder.mutation<
      UpdateQuickViewPanelsApiResponse,
      UpdateQuickViewPanelsApiArg
    >({
      query: ({ quickViewId, panels }) => ({
        version: 1,
        path: `analytics/quick-views/${quickViewId}/panels`,
        method: 'PUT',
        body: {
          panels: transformUpdateQuickViewPanelsArg(panels),
        },
      }),
      transformResponse: transformReadQuickViewResponse,
      // Handles optimistic update of the cached API data.
      async onQueryStarted(
        { quickViewId, panels },
        { dispatch, queryFulfilled },
      ) {
        const patchReadQuickView = dispatch(
          analyticsApi.util.updateQueryData(
            'readQuickView',
            { id: quickViewId },
            (draft) => {
              const mutableQuickView = draft.data.quick_view as DeepWritable<
                typeof draft.data.quick_view
              >;

              // Remove the panels from the cache that are not part of the updated panels.
              mutableQuickView.panels = mutableQuickView.panels.filter(
                ({ instance_id }) =>
                  panels.some((panel) => panel.instance_id === instance_id),
              );

              // Update the relevant fields of the panels that are part of the updated panels.
              // We prefer mutating the existing panel objects instead of replacing them to avoid
              // unnecessary re-renders caused by changed object references.
              for (const panel of mutableQuickView.panels) {
                const updatedPanel = panels.find(
                  ({ instance_id }) => instance_id === panel.instance_id,
                );

                if (updatedPanel) {
                  panel.controls = updatedPanel.controls as DeepWritable<
                    typeof panel.controls
                  >;
                  panel.layout = updatedPanel.layout;
                  panel.description = updatedPanel.description;
                }
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadQuickView.undo();
        }
      },
    }),
    createPanel: builder.mutation<CreatePanelApiResponse, CreatePanelApiArg>({
      query: ({ panel: { dashboard_id, ...configuration } }) => ({
        version: 1,
        path: `analytics/panels`,
        method: 'POST',
        body: {
          dashboard_id,
          configuration,
        },
      }),
      // Handles pessimistic update of the cached API data.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const {
            data: {
              data: { panel: createdPanel },
            },
          } = await queryFulfilled;

          dispatch(
            analyticsApi.util.updateQueryData(
              'readDashboards',
              undefined,
              (draft) => {
                const dashboards = draft.data.dashboards as DeepWritable<
                  typeof draft.data.dashboards
                >;
                const dashboard = dashboards.find(
                  ({ id }) => id === arg.panel.dashboard_id,
                ) as DeepWritable<Dashboard>;

                if (dashboard) {
                  dashboard.panels.push({
                    ...(arg.panel as DeepWritable<typeof arg.panel>),
                    ...(createdPanel as DeepWritable<typeof createdPanel>),
                  });
                }
              },
            ),
          );
        } catch (error) {
          /** Do nothing */
        }
      },
    }),
    deletePanel: builder.mutation<DeletePanelApiResponse, DeletePanelApiArg>({
      query: ({ id }) => ({
        version: 1,
        path: `analytics/panels/${id}`,
        method: 'DELETE',
      }),
      // Handles optimistic update of the cached API data.
      async onQueryStarted({ id, dashboardId }, { dispatch, queryFulfilled }) {
        const patchReadDashboards = dispatch(
          analyticsApi.util.updateQueryData(
            'readDashboards',
            undefined,
            (draft) => {
              const dashboards = draft.data.dashboards as DeepWritable<
                typeof draft.data.dashboards
              >;
              const dashboard = dashboards.find(
                ({ id }) => id === dashboardId,
              ) as DeepWritable<Dashboard>;
              const index = dashboard.panels.findIndex(
                (panel) => panel.id === id,
              );

              if (index !== -1) {
                dashboard.panels.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadDashboards.undo();
        }
      },
    }),
    readRewindData: builder.query<
      ReadRewindDataApiResponse,
      ReadRewindDataApiArg
    >({
      query: (arg) => ({
        version: 1,
        path: `analytics/rewind`,
        method: 'POST',
        body: transformReadInstrumentsArg({ id: '', ...arg }),
      }),
    }),
  }),
});
